import { useTranslations } from 'use-intl';
import { ConsentApiClient } from '@/clients/consentApiClient';
import {
  useScaChallengePolling,
  type ScaChallengeInitialData,
} from '@/hooks/useScaChallengeState';
import { isTerminalConsentFailure } from '@/lib/consentStatus';

/**
 * Polls consent authorization status. All the SCA-challenge machinery (QR rendering, flow
 * detection, the interval, consecutive-error counting, the arrival timeout) lives in
 * useScaChallengePolling, shared with usePaymentPolling — see that file for why.
 */
export const useConsentPolling = (
  consentId: string,
  authorisationId: string,
  bic: string,
  initialData: ScaChallengeInitialData,
  ref?: string,
  countryCode?: string
) => {
  const t = useTranslations('consultantBank.consentPolling');
  return useScaChallengePolling(initialData, {
    // `consentId` alone, never `&& bic` (FIN-783): a bic-less bank's bic is the legitimate
    // empty-string sentinel, not "polling hasn't started yet". consentId is what actually means
    // there is something to poll for, since it only exists once initiation has returned one.
    // Gating on the falsy bic instead meant a bic-less bank never polled at all AND never fired
    // the arrival timeout either — no poll, no timeout, no error, forever.
    enabled: !!consentId,
    defaultIntervalMs: 1000,
    label: 'consent',
    arrivalTimeoutMessage: t('timeout'),
    arrivalTimeoutDescription: `Consent polling: no completion signal for consentId=${consentId}`,
    arrivalTimeoutContext: 'useConsentPolling: arrival timeout',
    failedMessage: t('failed'),
    fetchStatus: async () => {
      const status = await ConsentApiClient.getConsentInitiationStatus(consentId, authorisationId, bic, ref, countryCode);
      return { ...status, isFailed: !status.isCompleted && isTerminalConsentFailure(status) };
    },
    identityKey: [consentId, authorisationId, bic, ref, countryCode].join('|'),
  });
};
